import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  PrismButton,
  PrismCheckbox,
  PrismDialog,
  PrismInputText,
  PrismInputTextarea,
  PrismSelect,
  PrismTypeahead,
} from "@prism-ui/react";
import { toast } from "../Toaster/toast";
import { getProgramUsers } from "../../services/users";
import { createProjectFairProject } from "../../services/projectFairDetails";
import PermissionLevel from "../../data/PermissionLevel";
import type { Project } from "./projects.utils";
import "./ProjectEditorModal.css";

const TEAM_ROLE_OPTIONS = [
  { value: "Mentor", display: "Mentor" },
  { value: "Mentee", display: "Mentee" },
  { value: "Committee Member", display: "Committee Member" },
];

type TeamMemberDraft = { name: string; role: string; title: string; triad?: number };
type ExecSponsorDraft = { name: string; title: string };
type ProjectDraft = {
  title: string;
  description: string;
  active: boolean;
  video_url: string;
  video_stream_url: string;
  spark_url: string;
  thanks: string;
  tags: string[];
  team: TeamMemberDraft[];
  exec_sponsor: ExecSponsorDraft[];
};

type EditableProject = Project & {
  logo?: string;
  video_url?: string;
  video_stream_url?: string;
  spark_url?: string;
  thanks?: string;
  chapter_id?: number;
};

interface ProjectEditorModalProps {
  open: boolean;
  mode: "create" | "edit";
  /** Required in create mode: which project-fair event the new project is added to. */
  eventId?: number;
  /** Required in edit mode: the project being edited. */
  project?: EditableProject;
  /** Drives the Chapter field in create mode: officers get a locked display of their own
   *  chapter, admins get a selector over every chapter. */
  currentUser?: { chapter_id?: number; Role?: { permission_level?: number } };
  onClose: () => void;
  onSaved: (project: any) => void;
}

const normalizeTeamRole = (role?: string) => {
  const normalized = role?.trim().toLowerCase();
  if (normalized === "mentee") return "Mentee";
  if (
    normalized === "committee" ||
    normalized === "committee member" ||
    normalized === "committee mentor" ||
    normalized === "tcm"
  ) {
    return "Committee Member";
  }
  return "Mentor";
};

const normalizeTriad = (triad: unknown) => {
  const value = Number(triad);
  return Number.isInteger(value) && value > 0 ? value : undefined;
};

const blankTeamMember = (triad?: number): TeamMemberDraft => ({ name: "", role: "Mentor", title: "", triad });
const blankExecSponsor = (): ExecSponsorDraft => ({ name: "", title: "" });

export const inferTeamTriads = (members: TeamMemberDraft[]): TeamMemberDraft[] => {
  if (members.some(({ triad }) => triad)) return members;

  const menteeCount = members.filter(({ role }) => role === "Mentee").length;
  if (menteeCount === 0) return members;

  const roleOrdinals = new Map<string, number>();
  return members.map((member) => {
    const ordinal = (roleOrdinals.get(member.role) ?? 0) + 1;
    roleOrdinals.set(member.role, ordinal);
    return { ...member, triad: Math.min(ordinal, menteeCount) };
  });
};

const draftFromProject = (project?: EditableProject): ProjectDraft => ({
  title: project?.title ?? "",
  description: project?.description ?? "",
  active: project?.active !== false,
  video_url: project?.video_url ?? "",
  video_stream_url: project?.video_stream_url ?? "",
  spark_url: project?.spark_url ?? "",
  thanks: project?.thanks ?? "",
  tags: (project?.tags ?? []).map((tagEntry) => tagEntry.tag?.name ?? "").filter(Boolean),
  team: inferTeamTriads(
    (project?.team?.team ?? []).map((member) => ({
      name: member.name ?? "",
      role: normalizeTeamRole(member.role),
      title: member.title ?? "",
      triad: normalizeTriad(member.triad),
    }))
  ),
  exec_sponsor: (project?.exec_sponsor ?? []).map((sponsor) => ({
    name: sponsor.name ?? "",
    title: sponsor.title ?? "",
  })),
});

/**
 * Shared create/edit surface for a single project-fair project. Used by:
 * - ProjectCard (public page, officer "Edit" action)
 * - ProjectFair (public page, officer "+ Add Project" action -> mode="create")
 * - ProjectFairs admin pane (officer/admin "+ Add new project" and per-row "Edit")
 *
 * Exposes full field parity with the database representation (title, description,
 * links, thanks, tags, team members with role+title, exec sponsors) per project-fair-admin
 * design D6, and stays open/inline (no page navigation or list re-sort) per design D5.
 */
const ProjectEditorModal = ({ open, mode, eventId, project, currentUser, onClose, onSaved }: ProjectEditorModalProps) => {
  const [draft, setDraft] = useState(() => draftFromProject(project));
  const [tagCatalog, setTagCatalog] = useState<Array<{ id: number; name: string }>>([]);
  const [programUsers, setProgramUsers] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const isAdmin = (currentUser?.Role?.permission_level ?? 0) >= PermissionLevel.Admin;
  // Create-mode only: officers are locked to their own chapter, admins may assign any
  // chapter. `chapters` is fetched from the same endpoint OfficerManagement uses to show
  // an officer's chapter name, so this reuses the existing role/chapter source of truth
  // rather than inventing a new one.
  const [chapters, setChapters] = useState<Array<{ id: number; location: string }>>([]);
  const [chapterId, setChapterId] = useState<number | null>(currentUser?.chapter_id ?? null);
  // The bulk importer writes `logo`, but the editor had no way to set or replace one, so an
  // import that mis-paired a logo could only be corrected by re-importing. Held separately
  // from `draft` because it ships as raw bytes to the logo endpoint, not as a column on the
  // project PUT.
  const [logoUpload, setLogoUpload] = useState<{ name: string; data: string } | null>(null);

  // Every ProjectCard renders one of these, so on /projects the grid would otherwise mount
  // a full PrismDialog — roughly fourteen Prism custom elements, each with its own shadow
  // root — for every project on the page, none of them open. Measured in the projects-grid
  // design-lab harness: six cards mounted six dialogs and eighty-five form controls. Across
  // a real fair archive that is thousands of idle custom elements, which is what exhausts
  // memory when the page is interacted with. Latch on first open instead of unmounting on
  // close, so the dialog's own open/close transition still runs normally afterwards.
  const [everOpened, setEverOpened] = useState(open);
  useEffect(() => {
    if (open) setEverOpened(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setDraft(draftFromProject(project));
    setLogoUpload(null);
    if (mode === "create") setChapterId(currentUser?.chapter_id ?? null);
  }, [open, project, mode, currentUser?.chapter_id]);

  useEffect(() => {
    if (!open || mode !== "create") return;
    axios
      .get("/api/chapters")
      .then((response) => setChapters(Array.isArray(response.data) ? response.data : []))
      .catch(() => setChapters([]));
  }, [open, mode]);

  // Admins without their own chapter_id (rare) get the alphabetically-first chapter once
  // the list loads, so the field is never left unselected.
  useEffect(() => {
    if (isAdmin && chapterId == null && chapters.length) setChapterId(chapters[0].id);
  }, [isAdmin, chapterId, chapters]);

  useEffect(() => {
    if (!open) return;
    axios
      .get("/api/tags")
      .then((response) => {
        const tags = Array.isArray(response.data) ? response.data : [];
        setTagCatalog(tags.filter((tag) => !tag.isHidden).map((tag) => ({ id: tag.id, name: tag.name })));
      })
      .catch(() => setTagCatalog([]));
    getProgramUsers()
      .then((users: any) => setProgramUsers(Array.isArray(users) ? users : []))
      .catch(() => setProgramUsers([]));
  }, [open]);

  const userOptions = useMemo(() => {
    const seen = new Map<string, { label: string; value: string }>();
    for (const entry of programUsers) {
      const name = entry?.User?.preferred_name || entry?.User?.name;
      if (!name) continue;
      if (!seen.has(name)) seen.set(name, { label: name, value: name });
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [programUsers]);

  const suggestedTagNames = useMemo(() => {
    const names = new Set(tagCatalog.map((tag) => tag.name));
    for (const name of draft.tags) names.add(name);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [tagCatalog, draft.tags]);

  const triadNumbers = useMemo(
    () =>
      [...new Set(draft.team.map(({ triad }) => triad).filter((triad): triad is number => !!triad))].sort(
        (a, b) => a - b
      ),
    [draft.team]
  );

  const triadOptions = useMemo(
    () => {
      const menteeCount = draft.team.filter(({ role }) => role === "Mentee").length;
      const maximumTriad = Math.max(2, triadNumbers[triadNumbers.length - 1] ?? 0, menteeCount);
      return [
        { value: "", display: "Unassigned" },
        ...Array.from({ length: maximumTriad }, (_, index) => ({
          value: String(index + 1),
          display: `Triad ${index + 1}`,
        })),
      ];
    },
    [draft.team, triadNumbers]
  );

  // Reads the chosen file as base64 for the logo endpoint, which takes bytes rather than
  // multipart so the same route can serve both this and the archive importer.
  const readLogoFile = (file?: File) => {
    if (!file) {
      setLogoUpload(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      setLogoUpload({ name: file.name, data: result.slice(result.indexOf(",") + 1) });
    };
    reader.onerror = () => toast.error("Could not read that image.");
    reader.readAsDataURL(file);
  };

  /**
   * Uploads a replacement logo, if one was chosen. Failures are surfaced but do not fail the
   * whole save: the project's text edits are already committed by then, and reporting the
   * save as failed would push the operator into re-entering work that did land.
   */
  const uploadLogo = async (projectId: number | string, fairId?: number) => {
    if (!logoUpload || !fairId) return undefined;
    try {
      const { data } = await axios.post(`/api/project_fair/events/${fairId}/logos/${projectId}`, {
        data: logoUpload.data,
        source_name: logoUpload.name,
      });
      return data?.logo as string | undefined;
    } catch (error) {
      toast.error("Project saved, but the logo could not be uploaded.");
      return undefined;
    }
  };

  const createTagIfMissing = async (tagName: string) => {    const trimmed = tagName.trim();
    if (!trimmed) return;
    const existing = tagCatalog.find((tag) => tag.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      setDraft((previous) => ({
        ...previous,
        tags: previous.tags.includes(existing.name) ? previous.tags : [...previous.tags, existing.name],
      }));
      return;
    }
    try {
      const response = await axios.post("/api/tags", { name: trimmed });
      const created = response.data;
      setTagCatalog((previous) => [...previous, { id: created.id, name: created.name }]);
      setDraft((previous) => ({
        ...previous,
        tags: previous.tags.includes(created.name) ? previous.tags : [...previous.tags, created.name],
      }));
      toast.success(`Tag \u201c${created.name}\u201d created.`);
    } catch (error) {
      toast.error(`Unable to create tag \u201c${trimmed}\u201d.`);
    }
  };

  const updateTeamMember = (index: number, patch: Partial<TeamMemberDraft>) => {
    setDraft((previous) => ({
      ...previous,
      team: previous.team.map((member, i) => (i === index ? { ...member, ...patch } : member)),
    }));
  };

  const removeTeamMember = (index: number) => {
    setDraft((previous) => ({ ...previous, team: previous.team.filter((_, i) => i !== index) }));
  };

  const updateExecSponsor = (index: number, patch: Partial<ExecSponsorDraft>) => {
    setDraft((previous) => ({
      ...previous,
      exec_sponsor: previous.exec_sponsor.map((sponsor, i) => (i === index ? { ...sponsor, ...patch } : sponsor)),
    }));
  };

  const removeExecSponsor = (index: number) => {
    setDraft((previous) => ({ ...previous, exec_sponsor: previous.exec_sponsor.filter((_, i) => i !== index) }));
  };

  const buildTagPayload = () =>
    [
      ...new Set(
        draft.tags
          .map((tagName) => tagCatalog.find((tag) => tag.name.toLowerCase() === tagName.toLowerCase())?.id)
          .filter((id): id is number => typeof id === "number")
      ),
    ].map((tag_id) => ({ tag_id }));

  const buildTeamPayload = () =>
    draft.team
      .map((member) => ({
        name: member.name.trim(),
        role: member.role,
        title: member.title.trim(),
        ...(member.triad ? { triad: member.triad } : {}),
      }))
      .filter((member) => member.name);

  const buildExecSponsorPayload = () =>
    draft.exec_sponsor
      .map((sponsor) => {
        const name = sponsor.name.trim();
        const title = sponsor.title.trim();
        return { name, ...(title ? { title } : {}) };
      })
      .filter((sponsor) => sponsor.name);

  const renderTeamMember = (member: TeamMemberDraft, index: number) => (
    <div key={index} className="project-editor-team-row">
      <PrismTypeahead
        label="Name"
        as="single"
        add-option
        options={userOptions}
        value={member.name}
        onPrismChange={(event: CustomEvent<{ value?: string }>) =>
          updateTeamMember(index, { name: event.detail?.value ?? "" })
        }
      />
      <PrismSelect
        label="Role"
        value={member.role}
        options={TEAM_ROLE_OPTIONS}
        onPrismChange={(event: CustomEvent<{ value?: string }>) =>
          updateTeamMember(index, { role: event.detail?.value ?? "Mentor" })
        }
      />
      <PrismInputText
        label="Title"
        placeholder="Title"
        value={member.title}
        onPrismInput={(event: CustomEvent<{ value?: string }>) =>
          updateTeamMember(index, { title: event.detail?.value ?? "" })
        }
      />
      <PrismSelect
        label="Triad"
        value={member.triad ? String(member.triad) : ""}
        options={triadOptions}
        onPrismChange={(event: CustomEvent<{ value?: string }>) =>
          updateTeamMember(index, { triad: normalizeTriad(event.detail?.value) })
        }
      />
      <PrismButton
        className="project-editor-remove"
        tone="secondary"
        display="ghost"
        size="sm"
        onPrismClick={() => removeTeamMember(index)}
      >
        Remove
      </PrismButton>
    </div>
  );

  const save = async () => {
    if (!draft.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      const tags = buildTagPayload();
      const team = buildTeamPayload();
      const exec_sponsor = buildExecSponsorPayload();

      if (mode === "create") {
        if (!eventId) {
          toast.error("Cannot add a project without an event.");
          return;
        }
        const created = await createProjectFairProject(eventId, {
          title: draft.title.trim(),
          description: draft.description,
          active: draft.active,
          video_url: draft.video_url,
          video_stream_url: draft.video_stream_url,
          spark_url: draft.spark_url,
          thanks: draft.thanks,
          tags,
          team,
          exec_sponsor,
          // Only admins can actually redirect this — the server re-derives it from the
          // event for officers regardless of what's sent here.
          chapter_id: isAdmin ? chapterId ?? undefined : undefined,
        });
        toast.success("Project added.");
        const createdLogo = await uploadLogo(created?.id, eventId);
        onSaved(createdLogo ? { ...created, logo: createdLogo } : created);      } else {
        if (!project?.id) return;
        const chapterId = project.chapter_id ?? (project as any)?.Chapter?.id;
        const payload = {
          ...project,
          ...draft,
          tags,
          team: { team },
          exec_sponsor,
          active: !!draft.active,
          year: project.year,
          chapter_id: chapterId,
        };

        const response = await axios.put(`/api/projects/${project.id}`, payload);
        toast.success("Project saved.");
        const savedLogo = await uploadLogo(project.id, (project as any)?.project_fair_id);
        onSaved({
          ...project,
          ...draft,
          active: !!draft.active,
          team: { team },
          exec_sponsor,
          tags: draft.tags.map((tagName) => ({
            tag: {
              id: tagCatalog.find((tag) => tag.name.toLowerCase() === tagName.toLowerCase())?.id ?? 0,
              name: tagName,
            },
          })),
          ...(response.data && typeof response.data === "object" ? response.data : {}),
          ...(savedLogo ? { logo: savedLogo } : {}),
        });
      }
      onClose();
    } catch (error) {
      toast.error(mode === "create" ? "Unable to add project." : "Unable to save project.");
    } finally {
      setSaving(false);
    }
  };

  if (!everOpened) return null;

  return (
    <PrismDialog
      open={open}
      close-method="user"
      onPrismClose={onClose}
      setAriaLabel={mode === "create" ? "Add project" : "Edit project"}
    >
      <span slot="header">{mode === "create" ? "Add Project" : "Edit Project"}</span>
      <div className="project-editor-body">
        <section className="project-editor-tile">
          <h3>Project details</h3>
          {mode === "create" &&
            (isAdmin ? (
              <PrismSelect
                label="Chapter"
                value={chapterId != null ? String(chapterId) : ""}
                options={chapters.map((chapter) => ({ value: String(chapter.id), display: chapter.location }))}
                onPrismChange={(event: CustomEvent<{ value?: string }>) =>
                  setChapterId(event.detail?.value ? Number(event.detail.value) : null)
                }
              />
            ) : (
              <p className="project-editor-help">
                Chapter: {chapters.find((chapter) => chapter.id === chapterId)?.location || "Your chapter"}
              </p>
            ))}
          <PrismInputText
            label="Title"
            value={draft.title}
            required
            onPrismInput={(event: CustomEvent<{ value?: string }>) =>
              setDraft((previous) => ({ ...previous, title: event.detail?.value ?? "" }))
            }
          />
          <PrismInputTextarea
            label="Description"
            value={draft.description}
            onPrismInput={(event: CustomEvent<{ value?: string }>) =>
              setDraft((previous) => ({ ...previous, description: event.detail?.value ?? "" }))
            }
          />
          <PrismInputText
            label="Teams Invite URL"
            value={draft.video_url}
            onPrismInput={(event: CustomEvent<{ value?: string }>) =>
              setDraft((previous) => ({ ...previous, video_url: event.detail?.value ?? "" }))
            }
          />
          <PrismInputText
            label="Presentation Replay Stream URL"
            value={draft.video_stream_url}
            onPrismInput={(event: CustomEvent<{ value?: string }>) =>
              setDraft((previous) => ({ ...previous, video_stream_url: event.detail?.value ?? "" }))
            }
          />
          <PrismInputText
            label="Spark URL"
            value={draft.spark_url}
            onPrismInput={(event: CustomEvent<{ value?: string }>) =>
              setDraft((previous) => ({ ...previous, spark_url: event.detail?.value ?? "" }))
            }
          />
          <PrismInputTextarea
            label="Special Thanks"
            value={draft.thanks}
            onPrismInput={(event: CustomEvent<{ value?: string }>) =>
              setDraft((previous) => ({ ...previous, thanks: event.detail?.value ?? "" }))
            }
          />
          <div className="project-editor-logo">
            <label htmlFor="project-editor-logo-input">Logo</label>
            {(logoUpload || (project as any)?.logo) && (
              <p className="project-editor-logo-current">
                {logoUpload ? `New: ${logoUpload.name}` : `Current: ${(project as any).logo}`}
              </p>
            )}
            <input
              id="project-editor-logo-input"
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              onChange={(event) => readLogoFile(event.target.files?.[0])}
            />
            <p className="project-editor-hint">
              Replaces the logo the importer paired with this project. Leave empty to keep the
              current one.
            </p>
          </div>
          <PrismCheckbox
            label="Visible to public"
            checked={draft.active}
            onPrismChange={(event: CustomEvent<{ checked?: boolean }>) =>
              setDraft((previous) => ({ ...previous, active: !!event.detail?.checked }))
            }
          />
        </section>

        <section className="project-editor-tile">
          <h3>People</h3>
          <div className="project-editor-subsection">
            <h4>Team members</h4>
            <p className="project-editor-help">
              A triad normally contains a Mentee, Mentor, and TCM, but incomplete and expanded triads are valid.
            </p>
            {triadNumbers.map((triad) => (
              <section key={triad} className="project-editor-triad">
                <h5>Triad {triad}</h5>
                {draft.team.map((member, index) => (member.triad === triad ? renderTeamMember(member, index) : null))}
              </section>
            ))}
            <section className="project-editor-triad project-editor-triad--unassigned">
              <h5>Unassigned people</h5>
              {draft.team.some(({ triad }) => !triad) ? (
                draft.team.map((member, index) => (!member.triad ? renderTeamMember(member, index) : null))
              ) : (
                <p className="project-editor-help">Everyone is assigned to a triad.</p>
              )}
            </section>
            <div className="project-editor-team-actions">
              <PrismButton
                tone="secondary"
                size="sm"
                onPrismClick={() =>
                  setDraft((previous) => ({ ...previous, team: [...previous.team, blankTeamMember()] }))
                }
              >
                + Add team member
              </PrismButton>
            </div>
          </div>

          <div className="project-editor-subsection project-editor-sponsors">
            <h4>Executive sponsors</h4>
            {draft.exec_sponsor.map((sponsor, index) => (
              <div key={index} className="project-editor-sponsor-row">
                <PrismTypeahead
                  label="Name"
                  as="single"
                  add-option
                  options={userOptions}
                  value={sponsor.name}
                  onPrismChange={(event: CustomEvent<{ value?: string }>) =>
                    updateExecSponsor(index, { name: event.detail?.value ?? "" })
                  }
                />
                <PrismInputText
                  label="Title"
                  value={sponsor.title}
                  onPrismInput={(event: CustomEvent<{ value?: string }>) =>
                    updateExecSponsor(index, { title: event.detail?.value ?? "" })
                  }
                />
                <PrismButton
                  className="project-editor-remove"
                  tone="secondary"
                  display="ghost"
                  size="sm"
                  onPrismClick={() => removeExecSponsor(index)}
                >
                  Remove
                </PrismButton>
              </div>
            ))}
            <PrismButton
              tone="secondary"
              size="sm"
              onPrismClick={() =>
                setDraft((previous) => ({ ...previous, exec_sponsor: [...previous.exec_sponsor, blankExecSponsor()] }))
              }
            >
              + Add exec sponsor
            </PrismButton>
          </div>
        </section>

        <section className="project-editor-tile">
          <h3>Tags</h3>
          <PrismInputText
            label="Tag list"
            value={draft.tags.join(", ")}
            onPrismInput={(event: CustomEvent<{ value?: string }>) =>
              setDraft((previous) => ({
                ...previous,
                tags: (event.detail?.value ?? "")
                  .split(",")
                  .map((value) => value.trim())
                  .filter(Boolean),
              }))
            }
          />
          <div className="project-editor-subsection">
            <h4>Suggested tags</h4>
            <div className="project-editor-tags">
            {suggestedTagNames.map((tagName) => (
              <PrismButton
                key={tagName}
                tone={draft.tags.includes(tagName) ? "primary" : "secondary"}
                size="sm"
                onPrismClick={() =>
                  setDraft((previous) => ({
                    ...previous,
                    tags: previous.tags.includes(tagName)
                      ? previous.tags.filter((value) => value !== tagName)
                      : [...previous.tags, tagName],
                  }))
                }
              >
                {tagName}
              </PrismButton>
            ))}
            </div>
          {draft.tags
            .filter((tagName) => !tagCatalog.some((tag) => tag.name.toLowerCase() === tagName.toLowerCase()))
            .map((tagName) => (
                <PrismButton key={tagName} tone="secondary" size="sm" onPrismClick={() => createTagIfMissing(tagName)}>
                  Create tag "{tagName}"
                </PrismButton>
            ))}
          </div>
        </section>
      </div>
      <PrismButton slot="footer" tone="secondary" display="outline" size="md" onPrismClick={onClose}>
        Cancel
      </PrismButton>
      <PrismButton
        slot="footer"
        tone="primary"
        display="fill"
        size="md"
        onPrismClick={save}
        disabled={saving}
        loading={saving}
      >
        <span>{mode === "create" ? "Add project" : "Save changes"}</span>
      </PrismButton>
    </PrismDialog>
  );
};

export default ProjectEditorModal;
